import _ from 'lodash';

import { bindServices } from '@/services/api';

const api = bindServices('lists');
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { updateFavorite } from './service';
import { filtersConfig } from './utils';
import { groupsFiltersConfig } from './manage-groups/utils';

import './utils';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  groups: [],
  favorites: [],
  currentGroup: {},
  searchCriteria: '',
  listPagination: meta,
  groupsPagination: meta,
  favoritesPagination: meta,
  orderBy: '',
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
  openListTaskTraceDrawer: false,
  listTaskTraceListId: null,
  // Launchpad Detalles / Editar metadata drawer
  showForm: false,
  formDetail: false,
  formId: null,
  formItem: null,
  formLoading: false,
  formSuccess: false,
};

const Model = {
  namespace: 'lists',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const params = {
          route: 'list/groups',
          ...CoreQueries.ManageLists.groups,
          ...payload,
        };

        const res = yield call(api.getAll, params);

        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    // Main management view (`/custom-lists` sidebar menu) →
    // `/list/manage`. Per the UX guide `/manage` is reserved for
    // top-level management screens accessed from the left sidebar;
    // every other context (selectors, pickers, embedded dialogs,
    // byList legacy) uses `/list/allowed`. `canManage` gates
    // configuration UI; `isFavorite` drives the star icon on each card.
    *fetchLists({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'list/manage',
          ...CoreQueries.ManageLists.paged,
          select: `${CoreQueries.ManageLists.paged.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryList',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Favorites-first load via the dedicated `/list/favorites`
    // endpoint. `canManage` is required for the row-command kebab and
    // `isFavorite` keeps the star icon filled (the endpoint returns
    // only favorites but the flag isn't auto-included in the select).
    *fetchFavorites({ payload }, { call, put }) {
      try {
        // Backend defaults to 5 items when $top/$skip are omitted.
        const res = yield call(api.getAll, {
          route: 'list/favorites',
          page: 1,
          limit: 500,
          ...CoreQueries.ManageLists.favorites,
          select: `${CoreQueries.ManageLists.favorites.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryFavorites',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Star toggle — mirrors the `/processes` contract:
    //   1. `querySetFavorite` flips the flag locally (optimistic).
    //   2. `markFavoritesChanged` flags the slice so the next time the
    //      user lands on the favorites tab, `onTabChange` refreshes it.
    // No re-fetch happens here: re-reading `/list/manage` or
    // `/list/favorites` right after the POST/DELETE clobbers the
    // optimistic update with potentially-stale replica data.
    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });
        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    // Only refresh the favorites tab when the slice has been touched
    // since the last fetch (`favoritesChanged`). Same contract as
    // `/processes`: a clean re-entry keeps the cached list intact.
    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.lists);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *paginate({ payload }, { put }) {
      yield put({ type: 'queryPage', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *createList({ payload }, { call, put }) {
      try {
        const res = yield call(api.create, {
          route: 'customList',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchLists', payload: { page: 1, readPriority: true } });
        return res;
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
        return null;
      }
    },

    *deleteItem({ id, params }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `customList/${id}`,
          method: 'delete',
        });
        yield put({
          type: 'queryDeleteItem',
          payload: { id },
        });
        yield put({
          type: 'fetchLists',
          payload: { ...params, readPriority: true },
        });
      } catch (e) {}
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `customList/activate/${payload.id}/${payload.isActive}`,
          method: 'post',
        });
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    *reset(o, { put }) {
      yield put({ type: 'resetState' });
    },

    *toggleListTaskTraceDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleListTaskTraceDrawer',
        payload,
      });
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!payload?.state || !payload?.id) return;
      // Same as useConfigForm: details always refetch; edit with a loaded item does not.
      if (payload.detail || _.isEmpty(payload.item)) {
        yield put({ type: 'fetchListItem', payload: { id: payload.id } });
      }
    },

    *fetchListItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'queryFormLoading', payload: true });
        const res = yield call(api.getById, {
          route: 'customList',
          id: payload.id,
          expand: {
            customListGroup: { select: 'id,name' },
            process: { select: 'id,name' },
            dynamicEntity: { select: 'id,displayName' },
            userCreator: { select: 'id,fullName' },
            userModifier: { select: 'id,fullName' },
          },
        });
        yield put({ type: 'queryFormItem', payload: res });
      } catch (e) {
        yield put({ type: 'queryFormLoading', payload: false });
      }
    },

    *updateListItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'queryFormLoading', payload: true });
        yield call(api.update, {
          route: 'customList',
          data: { ...payload, id: payload.id },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryFormSuccess' });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: false },
        });
      } catch (e) {
        yield put({ type: 'queryFormLoading', payload: false });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        groupsPagination: {
          ...state.groupsPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    queryList(state, { payload }) {
      return {
        ...state,
        data: payload?.items || [],
        listPagination: {
          ...state.listPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload?.items || [],
        favoritesPagination: {
          ...state.favoritesPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    // Optimistic favorite toggle: flips `isFavorite` on the main grid
    // (`data`) AND inserts/removes the item from the `favorites` slice
    // so the favorites tab reflects the change with zero network
    // traffic. The slice is reconciled the next time the user opens
    // the favorites tab (`onTabChange` → `fetchFavorites`).
    querySetFavorite(state, { payload }) {
      const { id, isFavorite } = payload;
      const next = !isFavorite;
      const updateItem = (item) => (item.id === id ? { ...item, isFavorite: next } : item);

      let favorites = state.favorites;
      if (next) {
        const target = state.data.find((it) => it.id === id);
        if (target && !favorites.some((f) => f.id === id)) {
          favorites = [...favorites, { ...target, isFavorite: true }];
        }
      } else {
        favorites = favorites.filter((f) => f.id !== id);
      }

      return {
        ...state,
        data: state.data.map(updateItem),
        favorites,
      };
    },

    markFavoritesChanged(state) {
      return { ...state, favoritesChanged: true };
    },

    clearFavoritesChanged(state) {
      return { ...state, favoritesChanged: false };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryDeleteItem(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id),
      };
    },

    resetState() {
      return initialState;
    },

    queryToggleListTaskTraceDrawer(state, { payload }) {
      return {
        ...state,
        openListTaskTraceDrawer: payload.state,
        listTaskTraceListId: payload.state ? payload.id : null,
      };
    },

    queryShowForm(state, { payload }) {
      const needsFetch =
        !!payload?.state && !!payload?.id && (!!payload.detail || _.isEmpty(payload.item));
      return {
        ...state,
        showForm: !!payload?.state,
        formDetail: !!payload?.detail,
        formId: payload?.state ? payload?.id ?? null : null,
        formItem: payload?.state ? payload.item ?? null : null,
        formSuccess: false,
        formLoading: needsFetch,
      };
    },

    queryFormLoading(state, { payload }) {
      return { ...state, formLoading: !!payload };
    },

    queryFormItem(state, { payload }) {
      return {
        ...state,
        formItem: payload,
        formLoading: false,
      };
    },

    queryFormSuccess(state) {
      return { ...state, formSuccess: true, formLoading: false };
    },

    clearFormSuccess(state) {
      return { ...state, formSuccess: false };
    },
  },
};

export default Model;
